import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { pocApi, frameImageURL, percent, LABEL_STYLE, SKIP_REASON_LABELS } from '../utils/pocApi';

// A null reason means that annotator did not skip the frame — the other one
// did. Printing an empty chip there would read as an unexplained skip.
const reasonText = (reason) => (reason === null || reason === undefined ? '判定' : (SKIP_REASON_LABELS[reason] || reason));

// PH0 and PH1 on one page, because they are read together: a detector that
// scores badly on a label the two annotators could not agree on has not been
// shown to be bad — the target was never defined.
//
// Both reports lead with their worst frames rather than their headline number.
// The number decides whether to continue; the frames are what tells you what to
// change.

const IOU_CHOICES = [0.3, 0.5, 0.7];

export default function PocReport() {
  useAuthGuard();
  const { datasetId } = useParams();

  const [dataset, setDataset] = useState(null);
  const [frames, setFrames] = useState([]);
  const [humanAuthors, setHumanAuthors] = useState([]);
  const [detectorModels, setDetectorModels] = useState([]);

  const [iou, setIou] = useState(0.5);
  const [label, setLabel] = useState('');
  const [authorA, setAuthorA] = useState('');
  const [authorB, setAuthorB] = useState('');
  const [truth, setTruth] = useState('');
  const [model, setModel] = useState('');
  const [minScore, setMinScore] = useState(0);

  const [agreement, setAgreement] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await pocApi.getDataset(datasetId);
        setDataset(data.dataset);
        setFrames(data.frames || []);
        setHumanAuthors(data.humanAuthors || []);
        setDetectorModels(data.detectorModels || []);
        setAuthorA((prev) => prev || data.humanAuthors?.[0] || '');
        setAuthorB((prev) => prev || data.humanAuthors?.[1] || '');
        setTruth((prev) => prev || data.humanAuthors?.[0] || '');
        setModel((prev) => prev || data.detectorModels?.[0] || '');
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [datasetId]);

  const loadAgreement = useCallback(async () => {
    // One annotator is enough to ask for the report. There is no agreement
    // rate to show, but "how far have I got and what did I rule out" is a real
    // question and the person working alone is the one who needs it.
    if (!authorA) { setAgreement(null); return; }
    try {
      const b = authorB && authorB !== authorA ? authorB : '';
      setAgreement(await pocApi.agreement(datasetId, { a: authorA, b, iou, label }));
    } catch (e) {
      setError(e.message);
    }
  }, [datasetId, authorA, authorB, iou, label]);

  const loadMetrics = useCallback(async () => {
    if (!truth || !model) { setMetrics(null); return; }
    try {
      setMetrics(await pocApi.metrics(datasetId, { gt: truth, model, iou, label, minScore }));
    } catch (e) {
      setError(e.message);
    }
  }, [datasetId, truth, model, iou, label, minScore]);

  useEffect(() => { loadAgreement(); }, [loadAgreement]);
  useEffect(() => { loadMetrics(); }, [loadMetrics]);

  // Detector output arrives as a file from whoever ran the model, so the
  // console takes a paste rather than requiring a new upload path. Frames are
  // addressed by seq — the detector never sees our uuids.
  const runImport = async () => {
    setImporting(true);
    setError('');
    setImportResult('');
    try {
      const parsed = JSON.parse(importText);
      const modelName = parsed.model || model;
      if (!modelName) throw new Error('モデル名がありません（JSON の "model" か、下の選択で指定してください）');

      const bySeq = new Map(frames.map((f) => [f.seq, f]));
      let written = 0;
      let skipped = 0;

      for (const entry of parsed.frames || []) {
        const frame = bySeq.get(entry.seq);
        if (!frame) { skipped++; continue; }
        await pocApi.saveBoxes(frame.id, {
          source: 'detector',
          author: modelName,
          boxes: (entry.boxes || []).map((b) => ({
            label: b.label,
            score: b.score ?? null,
            x: b.x, y: b.y, w: b.w, h: b.h,
          })),
        });
        written++;
      }

      setImportResult(`${written} フレームに取り込みました${skipped ? `（seq 不一致 ${skipped} 件は無視）` : ''}`);
      setDetectorModels((prev) => (prev.includes(modelName) ? prev : [...prev, modelName]));
      setModel(modelName);
      await loadMetrics();
    } catch (e) {
      setError(e.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0">{dataset?.name} <span className="text-muted">— PH0 / PH1 レポート</span></h5>
          <small className="text-muted">フレーム {frames.length} 件</small>
        </div>
        <div className="d-flex gap-2">
          <Link className="btn btn-sm btn-outline-secondary" to={`/poc/${datasetId}/annotate`}>アノテーション</Link>
          <Link className="btn btn-sm btn-outline-secondary" to={`/poc/${datasetId}/coverage`}>PH1.5</Link>
          <Link className="btn btn-sm btn-outline-secondary" to="/poc">一覧</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="card mb-4">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-auto">
              <label className="form-label small mb-1">IoU 閾値</label>
              <div className="btn-group btn-group-sm d-block">
                {IOU_CHOICES.map((v) => (
                  <button
                    key={v}
                    className={`btn ${iou === v ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => setIou(v)}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-auto">
              <label className="form-label small mb-1">対象ラベル</label>
              <select className="form-select form-select-sm" value={label} onChange={(e) => setLabel(e.target.value)}>
                <option value="">すべて</option>
                {Object.entries(LABEL_STYLE).map(([key, v]) => (
                  <option key={key} value={key}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
            小さい対象ほど IoU が落ちやすいので、閾値を動かして同じ土俵で比べます。景品ブロックは Cabinet より小さく、Control Panel より大きい。
          </div>
        </div>
      </div>

      {/* ---- PH0 ---- */}
      <h6 className="mb-2">PH0 — アノテータ間一致</h6>
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-2 align-items-end mb-3">
            <div className="col-md-3">
              <label className="form-label small mb-1">アノテータ A</label>
              <select className="form-select form-select-sm" value={authorA} onChange={(e) => setAuthorA(e.target.value)}>
                <option value="">—</option>
                {humanAuthors.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">アノテータ B</label>
              <select className="form-select form-select-sm" value={authorB} onChange={(e) => setAuthorB(e.target.value)}>
                <option value="">（なし・1名で確認）</option>
                {humanAuthors.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>

          {!agreement ? (
            <div className="text-muted small">
              アノテータを選ぶと状況が出ます。現在: {humanAuthors.join(', ') || 'なし'}
            </div>
          ) : agreement.solo ? (
            <>
              <div className="row g-3 mb-3">
                <Stat
                  label="完了フレーム"
                  value={`${agreement.scoredFrames} 件`}
                  hint={`未着手 ${agreement.pendingFrames} 件 ・ 除外 ${agreement.skippedFrames || 0} 件`}
                />
                <Stat
                  label="描いた枠"
                  value={`${agreement.boxCounts?.total ?? 0} 件`}
                  hint={`Panel ${agreement.boxCounts?.panel ?? 0} ・ Cabinet ${agreement.boxCounts?.cabinet ?? 0}`}
                />
                <Stat label="一致率" value="—" hint="2名分が揃うと出ます" />
              </div>

              {Object.keys(agreement.skipReasons || {}).length > 0 && (
                <div className="small text-muted mb-3">
                  除外の内訳：
                  {Object.entries(agreement.skipReasons).map(([key, n]) => (
                    <span key={key} className="badge bg-danger-subtle text-danger-emphasis ms-1">
                      {SKIP_REASON_LABELS[key] || key} {n}
                    </span>
                  ))}
                </div>
              )}

              <div className="small text-muted mb-2">完了したフレーム</div>
              <FrameStrip rows={agreement.frames} caption={(r) => `bbox ${r.boxes} 件`} />
            </>
          ) : (
            <>
              <div className="row g-3 mb-3">
                <Stat label="一致率" value={percent(agreement.overall?.rate)} hint="2×一致数 / 両者の総bbox数" />
                <Stat label="一致bboxの平均IoU" value={agreement.overall?.meanIoU?.toFixed(3) ?? '—'} hint="対応が付いた枠の重なり" />
                <Stat
                  label="採点フレーム"
                  value={`${agreement.scoredFrames} 件`}
                  hint={`未完了 ${agreement.pendingFrames} 件 ・ 除外 ${agreement.skippedFrames || 0} 件は対象外`}
                />
                <Stat
                  label="片方だけが描いた枠"
                  value={`${(agreement.overall?.onlyLeft || 0)} / ${(agreement.overall?.onlyRight || 0)}`}
                  hint={`${authorA} のみ / ${authorB} のみ`}
                />
              </div>

              <div className="table-responsive mb-3">
                <table className="table table-sm mb-0">
                  <thead className="table-light">
                    <tr><th>ラベル</th><th className="text-end">一致率</th><th className="text-end">平均IoU</th><th className="text-end">A のみ</th><th className="text-end">B のみ</th></tr>
                  </thead>
                  <tbody>
                    {['panel', 'cabinet'].map((key) => {
                      const row = agreement.byLabel?.[key];
                      if (!row) return null;
                      return (
                        <tr key={key}>
                          <td><span style={{ color: LABEL_STYLE[key].color }}>■</span> {LABEL_STYLE[key].label}</td>
                          <td className="text-end">{percent(row.rate)}</td>
                          <td className="text-end">{row.meanIoU?.toFixed(3) ?? '—'}</td>
                          <td className="text-end">{row.onlyLeft}</td>
                          <td className="text-end">{row.onlyRight}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="small text-muted mb-2">一致しなかったフレーム（定義が揺れている箇所）</div>
              <FrameStrip
                rows={agreement.worstFrames}
                caption={(r) => `${percent(r.rate)} ・ A${r.onlyLeft}/B${r.onlyRight}`}
              />

              {(agreement.skipDisputes || []).length > 0 && (
                <>
                  {/* One person could judge the frame and the other could not.
                      That disagreement is about the material itself, and it is
                      the signal that tells us to change how we film or sample
                      rather than how we define a label. */}
                  <div className="small text-muted mt-3 mb-2">
                    片方だけが除外したフレーム（素材の判断が割れている箇所）
                  </div>
                  <FrameStrip
                    rows={agreement.skipDisputes}
                    caption={(r) => `A ${reasonText(r.a)} / B ${reasonText(r.b)}`}
                  />
                </>
              )}

              {Object.keys(agreement.skipReasons || {}).length > 0 && (
                <div className="small text-muted mt-3">
                  除外の内訳：
                  {Object.entries(agreement.skipReasons).map(([key, n]) => (
                    <span key={key} className="badge bg-danger-subtle text-danger-emphasis ms-1">
                      {SKIP_REASON_LABELS[key] || key} {n}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ---- PH1 ---- */}
      <h6 className="mb-2">PH1 — 検出器 vs 正解</h6>
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-2 align-items-end mb-3">
            <div className="col-md-3">
              <label className="form-label small mb-1">正解（GT）</label>
              <select className="form-select form-select-sm" value={truth} onChange={(e) => setTruth(e.target.value)}>
                <option value="">—</option>
                {humanAuthors.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">検出モデル</label>
              <select className="form-select form-select-sm" value={model} onChange={(e) => setModel(e.target.value)}>
                <option value="">—</option>
                {detectorModels.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">スコア下限 {minScore.toFixed(2)}</label>
              <input
                type="range" min="0" max="0.95" step="0.05"
                className="form-range"
                value={minScore}
                onChange={(e) => setMinScore(Number(e.target.value))}
              />
            </div>
            <div className="col-md-3 text-end">
              <button className="btn btn-sm btn-outline-primary" onClick={() => setImportOpen((v) => !v)}>
                検出結果を取り込む
              </button>
            </div>
          </div>

          {importOpen && (
            <div className="border rounded p-3 mb-3 bg-light">
              <div className="small text-muted mb-2">
                検出器の出力を貼り付けます。座標はフレームに対する 0〜1 の正規化値、フレームは seq で指定します。
              </div>
              <pre className="small bg-white border rounded p-2 mb-2" style={{ fontSize: '0.7rem' }}>{`{
  "model": "grounding-dino-b",
  "frames": [
    { "seq": 1, "boxes": [ { "label": "panel", "score": 0.62, "x": 0.31, "y": 0.72, "w": 0.18, "h": 0.09 } ] }
  ]
}`}</pre>
              <textarea
                className="form-control form-control-sm mb-2"
                rows={6}
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="JSON を貼り付け"
              />
              <div className="d-flex align-items-center gap-2">
                <button className="btn btn-sm btn-primary" onClick={runImport} disabled={importing || !importText.trim()}>
                  {importing ? '取り込み中…' : '取り込む'}
                </button>
                {importResult && <span className="small text-success">{importResult}</span>}
              </div>
            </div>
          )}

          {!metrics ? (
            <div className="text-muted small">
              GT と検出モデルを選ぶと Precision / Recall が出ます。
              {detectorModels.length === 0 && ' 検出結果はまだ投入されていません。'}
            </div>
          ) : (
            <>
              <div className="row g-3 mb-3">
                <Stat label="Precision" value={percent(metrics.overall?.precision)} hint={`検出 ${metrics.overall.tp + metrics.overall.fp} 件中 ${metrics.overall.tp} 件が正解`} />
                <Stat label="Recall" value={percent(metrics.overall?.recall)} hint={`正解 ${metrics.overall.tp + metrics.overall.fn} 件中 ${metrics.overall.tp} 件を検出`} />
                <Stat label="F1" value={percent(metrics.overall?.f1)} hint="Precision と Recall の調和平均" />
                <Stat label="平均IoU" value={metrics.overall?.meanIoU?.toFixed(3) ?? '—'} hint="正検出のみの重なり" />
              </div>

              <div className="small text-muted mb-2">
                採点 {metrics.scoredFrames} フレーム
                {metrics.unannotatedFrames > 0 && `（GT 未完了 ${metrics.unannotatedFrames} 件は対象外）`}
                {metrics.excludedFrames > 0 && `（判断不能として除外 ${metrics.excludedFrames} 件）`}
                ・ 見逃し {metrics.overall.fn} ・ 誤検出 {metrics.overall.fp}
              </div>

              <div className="small text-muted mb-2">スコアが低かったフレーム</div>
              <FrameStrip
                rows={metrics.worstFrames}
                caption={(r) => `F1 ${percent(r.f1)} ・ 見逃し${r.fn}/誤検出${r.fp}`}
              />
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="col-6 col-md-3">
      <div className="border rounded p-3 h-100">
        <div className="text-muted" style={{ fontSize: '0.75rem' }}>{label}</div>
        <div className="fs-4 fw-semibold">{value}</div>
        <div className="text-muted" style={{ fontSize: '0.7rem' }}>{hint}</div>
      </div>
    </div>
  );
}

function FrameStrip({ rows, caption }) {
  if (!rows || rows.length === 0) return <div className="text-muted small">該当なし</div>;
  return (
    <div className="d-flex gap-2 flex-wrap">
      {rows.map((r) => (
        <div key={r.frameId} style={{ width: 130 }}>
          <img
            src={frameImageURL(r.imageUrl)}
            alt={`frame ${r.seq}`}
            className="rounded border"
            style={{ width: '100%', height: 90, objectFit: 'cover' }}
          />
          <div className="text-muted" style={{ fontSize: '0.7rem' }}>
            #{r.seq} ・ {Number(r.timestampSec).toFixed(1)}s
          </div>
          <div style={{ fontSize: '0.7rem' }}>{caption(r)}</div>
        </div>
      ))}
    </div>
  );
}
